<template>
  <div class="smart-graph-wrapper">
    <div v-if="activeVectorStoreId || hasExploreNodes">
      <div class="smart-graph-bar d-flex justify-content-between align-items-center px-3 py-1">
        <span class="smart-graph-status text-light small d-flex align-items-center" :title="activeGraphName">
          <i class="bx bxs-circle text-success me-1 font-size-xs"></i>
          <span class="text-truncate" style="max-width: 160px;">{{ activeGraphName }}</span>
        </span>
        <button
          class="ed-btn ed-btn__blim btn-sm py-0 px-2"
          :disabled="isSyncing"
          @click="syncGraphWithSmartGraph"
          title="Sync vector embeddings with current graph"
        >
          <i class="bx bx-sync me-1" :class="{ 'bx-spin': isSyncing }"></i> Sync
        </button>
      </div>
      <AIChatInterface
        :key="activeThreadId"
        :assistantId="$store.state.eddie.mainAssistantId || 'smartgraph_gemini'"
        :threadId="activeThreadId"
        :vectorStoreId="activeVectorStoreId"
      />
    </div>
    <div v-else class="smart-graph-empty p-4 text-center">
      <i class="bx bx-network-chart fs-1 text-muted mb-2"></i>
      <h6 class="text-light">SmartGraph Ready</h6>
      <p class="text-muted small">Explore or add nodes to your graph canvas to start chatting and generating nodes.</p>
    </div>
  </div>
</template>

<script>
import AIChatInterface from './AIChatInterface.vue';

export default {
  components: {
    AIChatInterface,
  },
  data() {
    return {
      isSyncing: false,
    };
  },
  computed: {
    isSmartGraph() {
      return Boolean(this.activeVectorStoreId);
    },
    graphDetailObject() {
      return this.$store.state.gmodule.graph_detail || {};
    },
    hasExploreNodes() {
      const nodes = this.$store.state.gmodule.g6_explore_data?.nodes;
      return Array.isArray(nodes) && nodes.length > 0;
    },
    sessionKey() {
      const user = this.$store.getters['auth/authUser'];
      const username = user?.username || 'user';
      const graphId = this.$store.state.active_graph_id?.explore_library;
      return graphId || username;
    },
    activeVectorStoreId() {
      const memory = this.graphDetailObject?.memory_id;
      if (memory && typeof memory === 'object' && memory.vector_store_id) {
        return memory.vector_store_id;
      }
      if (typeof this.$store.state.gmodule.active_vector_store === 'string') {
        return this.$store.state.gmodule.active_vector_store;
      }
      if (this.graphDetailObject?.graphId) {
        return `vec_${this.graphDetailObject.graphId}`;
      }
      return this.hasExploreNodes ? `vec_${this.sessionKey}` : null;
    },
    activeThreadId() {
      const memory = this.graphDetailObject?.memory_id;
      if (memory && typeof memory === 'object' && memory.thread_id) {
        return memory.thread_id;
      }
      if (this.graphDetailObject?.graphId) {
        return `thread_${this.graphDetailObject.graphId}`;
      }
      return `thread_${this.activeVectorStoreId || this.sessionKey}`;
    },
    activeGraphName() {
      return this.graphDetailObject?.graphName || 'Knowledge Graph';
    },
  },
  watch: {
    activeThreadId(newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.hasExploreNodes) {
        this.syncGraphWithSmartGraph();
      }
    },
    hasExploreNodes(val) {
      if (val) {
        this.syncGraphWithSmartGraph();
      }
    },
  },
  async mounted() {
    if (this.hasExploreNodes) {
      await this.syncGraphWithSmartGraph();
    }
  },
  methods: {
    async syncGraphWithSmartGraph() {
      this.isSyncing = true;
      try {
        await this.$store.dispatch('gmodule/syncGraphToSmartGraph', {
          vectorStoreId: this.activeVectorStoreId,
        });
      } catch (e) {
        console.warn('SmartGraph sync error:', e);
      } finally {
        this.isSyncing = false;
      }
    },
    setCurrentThought(graphId, memoryId, graphName, vectorStoreId) {
      const graphObj = {
        graphId,
        memory_id: {
          thread_id: memoryId,
          vector_store_id: vectorStoreId,
        },
        label: graphName,
        type: 'graph',
      };
      this.$store.dispatch('setCurrentThought', {
        thoughtObject: graphObj,
        excludeCallList: ['quests/questGraphAction', 'gmodule/exploreGraphFetch'],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.smart-graph-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.smart-graph-bar {
  background: rgba(0, 0, 0, 0.25);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.font-size-xs {
  font-size: 8px;
}
.smart-graph-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
